import { clsx } from 'clsx'
import type { DrawingTool } from '../types'

interface DrawingToolsProps {
  activeTool: DrawingTool
  onToolSelect: (tool: DrawingTool) => void
}

interface ToolGroup {
  label: string
  tools: { value: DrawingTool; label: string; icon: JSX.Element }[]
}

const toolGroups: ToolGroup[] = [
  {
    label: 'Cursor',
    tools: [
      {
        value: 'cursor',
        label: 'Cursor',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
          </svg>
        ),
      },
      {
        value: 'crosshair',
        label: 'Crosshair',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="22" y1="12" x2="18" y2="12" />
            <line x1="6" y1="12" x2="2" y2="12" />
            <line x1="12" y1="6" x2="12" y2="2" />
            <line x1="12" y1="22" x2="12" y2="18" />
          </svg>
        ),
      },
    ],
  },
  {
    label: 'Trend Lines',
    tools: [
      {
        value: 'trendline',
        label: 'Trend Line',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="19" x2="21" y2="5" />
          </svg>
        ),
      },
      {
        value: 'ray',
        label: 'Ray',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="19" x2="21" y2="5" />
            <circle cx="3" cy="19" r="2" fill="currentColor" />
          </svg>
        ),
      },
      {
        value: 'extendedLine',
        label: 'Extended Line',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="0" y1="22" x2="24" y2="2" strokeDasharray="2 2" />
            <line x1="5" y1="17" x2="19" y2="7" />
          </svg>
        ),
      },
      {
        value: 'horizontalLine',
        label: 'Horizontal Line',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="12" x2="21" y2="12" />
          </svg>
        ),
      },
      {
        value: 'verticalLine',
        label: 'Vertical Line',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="3" x2="12" y2="21" />
          </svg>
        ),
      },
    ],
  },
  {
    label: 'Channels',
    tools: [
      {
        value: 'parallelChannel',
        label: 'Parallel Channel',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="17" x2="21" y2="7" />
            <line x1="3" y1="21" x2="21" y2="11" />
          </svg>
        ),
      },
      {
        value: 'pitchfork',
        label: 'Pitchfork',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 21L12 3L21 21" />
            <line x1="12" y1="3" x2="12" y2="21" />
          </svg>
        ),
      },
    ],
  },
  {
    label: 'Fibonacci',
    tools: [
      {
        value: 'fibonacciRetracement',
        label: 'Fib Retracement',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="4" x2="21" y2="4" />
            <line x1="3" y1="8" x2="21" y2="8" strokeOpacity="0.7" />
            <line x1="3" y1="12" x2="21" y2="12" strokeOpacity="0.5" />
            <line x1="3" y1="16" x2="21" y2="16" strokeOpacity="0.3" />
            <line x1="3" y1="20" x2="21" y2="20" />
          </svg>
        ),
      },
      {
        value: 'fibonacciExtension',
        label: 'Fib Extension',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="20" x2="21" y2="20" />
            <line x1="3" y1="16" x2="21" y2="16" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="6" x2="21" y2="6" strokeDasharray="4 2" />
            <line x1="3" y1="2" x2="21" y2="2" strokeDasharray="4 2" />
          </svg>
        ),
      },
    ],
  },
  {
    label: 'Shapes',
    tools: [
      {
        value: 'rectangle',
        label: 'Rectangle',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="5" width="18" height="14" rx="1" />
          </svg>
        ),
      },
      {
        value: 'ellipse',
        label: 'Ellipse',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <ellipse cx="12" cy="12" rx="9" ry="6" />
          </svg>
        ),
      },
      {
        value: 'triangle',
        label: 'Triangle',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 3L21 21H3L12 3Z" />
          </svg>
        ),
      },
    ],
  },
  {
    label: 'Measure',
    tools: [
      {
        value: 'measure',
        label: 'Measure',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 21L21 3" />
            <path d="M3 21v-6m0 6h6" />
            <path d="M21 3v6m0-6h-6" />
          </svg>
        ),
      },
      {
        value: 'longPosition',
        label: 'Long Position',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="4" y="4" width="16" height="16" rx="1" fill="#00ff8830" />
            <path d="M12 16V8m0 0l3 3m-3-3l-3 3" />
          </svg>
        ),
      },
      {
        value: 'shortPosition',
        label: 'Short Position',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="4" y="4" width="16" height="16" rx="1" fill="#ff336630" />
            <path d="M12 8v8m0 0l3-3m-3 3l-3-3" />
          </svg>
        ),
      },
    ],
  },
  {
    label: 'Annotations',
    tools: [
      {
        value: 'text',
        label: 'Text',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 7V4h16v3" />
            <line x1="12" y1="4" x2="12" y2="20" />
            <line x1="8" y1="20" x2="16" y2="20" />
          </svg>
        ),
      },
      {
        value: 'arrow',
        label: 'Arrow',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="5" y1="19" x2="19" y2="5" />
            <polyline points="10,5 19,5 19,14" />
          </svg>
        ),
      },
      {
        value: 'brush',
        label: 'Brush',
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 19l7-7 3 3-7 7-3-3z" />
            <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
          </svg>
        ),
      },
    ],
  },
]

export function DrawingTools({ activeTool, onToolSelect }: DrawingToolsProps) {
  return (
    <div className="w-12 bg-kit-card border-r border-kit-border flex flex-col py-2 overflow-y-auto">
      {toolGroups.map((group, groupIndex) => (
        <div key={group.label}>
          {groupIndex > 0 && (
            <div className="mx-2 my-2 border-t border-kit-border" />
          )}
          {group.tools.map(({ value, label, icon }) => (
            <button
              key={value}
              onClick={() => onToolSelect(value)}
              title={label}
              className={clsx(
                'w-full p-2.5 flex justify-center items-center transition-all duration-200',
                activeTool === value
                  ? 'bg-kit-accent/20 text-kit-accent'
                  : 'text-gray-400 hover:text-white hover:bg-kit-darker'
              )}
            >
              {icon}
            </button>
          ))}
        </div>
      ))}
    </div>
  )
}
